import { useCallback, useEffect, useState } from 'react';
import { deleteRequest, getRequest, patchRequest, postRequest } from 'services/api';
import { LIMIT_DATA_PER_PAGE } from 'utils/constants';
import { buildQueryString } from 'utils/queryHelpers';
import { usePaginationWithComponent } from 'hooks/usePaginationWithComponent';

const defaultBankAccount = {
    data: [],
    pagination: null,
    error: null,
    loading: false
};

const defaultFilterOptions = {
    search: '',
    currentPage: 1,
    totalPages: 1,
};

const useBankAccounts = () => {
    const [bankAccounts, setBankAccounts] = useState(defaultBankAccount);

    const [filterOptions, setFilterOptions] = useState(defaultFilterOptions);

    const queryString = buildQueryString({ search: filterOptions.search || '', page: filterOptions.currentPage, limit: LIMIT_DATA_PER_PAGE });

    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            console.log('search', e.target.value);
            setFilterOptions((prevState) => ({
                ...prevState,
                search: e.target.value,
            }));
        }
    };

    const fetchBankAccounts = useCallback(async () => {
        setBankAccounts((prevState) => ({
            ...prevState,
            loading: true,
        }));

        try {
            const response = await getRequest(`/company-bank${queryString}`);
            if (response.success) {
                setBankAccounts({
                    data: response.data,
                    pagination: response.pagination,
                    error: null,
                    loading: false,
                });

                setFilterOptions((prev) => ({
                    ...prev,
                    totalPages: response.pagination.last_page
                }));
            }
        } catch (error) {
            setBankAccounts({
                data: [],
                pagination: null,
                error: error.message,
                loading: false,
            });
        }
    }, [queryString]);

    useEffect(() => {
        fetchBankAccounts();
    }, [queryString]);

    const handlePageChange = (page) => {
        setFilterOptions((prevState) => ({
            ...prevState,
            currentPage: page,
        }));
    };

    const totalPages = bankAccounts.pagination?.last_page || 1;

    const Pagination = usePaginationWithComponent({
        totalPages: totalPages,
        currentPage: filterOptions.currentPage,
        onPageChange: handlePageChange,
        visiblePages: 5, // Optional: number of visible pages in pagination (default is 5)
    });

    return { bankAccounts, setBankAccounts, handleSearch, filterOptions, Pagination, refetch: fetchBankAccounts };
};

export default useBankAccounts;

export const useCreateBankAccount = ({ bankAccounts, setBankAccounts }) => {
    const createBankAccount = async (data) => {
        setBankAccounts((prevState) => ({
            ...prevState,
            loading: true,
        }));
        try {
            const response = await postRequest('/company-bank', data);
            if (response.success) {
                setBankAccounts((prevState) => ({
                    ...prevState,
                    loading: false,
                }));
                return { success: true, message: 'Bank account has been created' };
            }
        } catch (error) {
            setBankAccounts((prevState) => ({
                ...prevState,
                loading: false,
            }));
            return { success: false, message: 'Failed to create bank account' };
        }
    };

    return { createBankAccount };
}

export const useUpdateBankAccount = ({ bankAccounts, setBankAccounts }) => {
    const updateBankAccount = async (id, data) => {
        setBankAccounts((prevState) => ({
            ...prevState,
            loading: true,
        }));
        try {
            const response = await patchRequest(`/company-bank/${id}`, data);
            if (response.success) {
                setBankAccounts((prevState) => ({
                    ...prevState,
                    loading: false,
                }));
                return { success: true, message: 'Bank account has been updated' };
            }
        } catch (error) {
            setBankAccounts((prevState) => ({
                ...prevState,
                loading: false,
            }));
            return { success: false, message: 'Failed to update bank account' };
        }
    };

    return { updateBankAccount };
};

export const useRemoveBankAccount = ({ bankAccounts, setBankAccounts }) => {
    const removeBankAccount = async (id) => {
        setBankAccounts((prevState) => ({
            ...prevState,
            loading: true,
        }));
        try {
            const response = await deleteRequest(`/company-bank/${id}`);
            if (response.success) {
                setBankAccounts((prevState) => ({
                    ...prevState,
                    loading: false,
                }));
                return { success: true, message: 'Bank account has been deleted' };
            }
        } catch (error) {
            setBankAccounts((prevState) => ({
                ...prevState,
                loading: false,
            }));
            return { success: false, message: 'Failed to delete bank account' };
        }
    };

    return { removeBankAccount };
};